/*
 * The web tracker's accounting, free of React and the DOM so the rules stay
 * unit-testable. Time counts only while the tab is visible, the window has
 * focus and there was input within the idle window (3 minutes; 8 while a
 * document is open, since reading a draft is quiet work; 20 while the user's
 * own agent turn streams, since watching Casey work is too). Active stretches
 * become segments; the server merges them into sessions and unions
 * overlapping tabs, so the client never has to coordinate between tabs.
 */
import {
  parseCasePath,
  parseOpenParam,
  CASE_HOME_SEGMENT
} from "../../caseIde/routes";
import type { BeatSegment, TrackerContext } from "../types";

export const IDLE_MS = 3 * 60_000;
export const IDLE_DOCUMENT_MS = 8 * 60_000;
/**
 * While the user's own agent turn streams, watching it is supervision, not
 * idleness; past this without input they have walked away.
 */
export const IDLE_AGENT_MS = 20 * 60_000;
/** Longest gap between ticks that still counts (sleep, throttled timers). */
export const MAX_TICK_MS = 30_000;

const DOCUMENT_KINDS = new Set(["draft", "file", "packet", "form"]);

/** The case and artifact a route addresses, or null for non-client pages. */
export const contextFromLocation = (
  pathname: string,
  search: string
): TrackerContext | null => {
  const parsed = parseCasePath(pathname);
  if (!parsed) return null;
  const [first, second] = parsed.segments;
  const open = first === CASE_HOME_SEGMENT ? parseOpenParam(search) : null;
  const surface = open?.kind ?? first ?? CASE_HOME_SEGMENT;
  const artifact = open
    ? `${open.kind}:${open.id}`
    : second
    ? `${first}:${second}`
    : null;
  return {
    individual_id: parsed.ref.individualId,
    foreign_national_id: parsed.ref.foreignNationalId ?? null,
    surface,
    artifact
  };
};

export const sameCase = (
  a: TrackerContext | null,
  b: TrackerContext | null
): boolean =>
  (a?.individual_id ?? null) === (b?.individual_id ?? null) &&
  (a?.foreign_national_id ?? null) === (b?.foreign_national_id ?? null);

const sameContext = (a: TrackerContext | null, b: TrackerContext | null) =>
  sameCase(a, b) &&
  (a?.surface ?? null) === (b?.surface ?? null) &&
  (a?.artifact ?? null) === (b?.artifact ?? null);

const caseKey = (ctx: TrackerContext | null): string | null =>
  ctx?.individual_id || ctx?.foreign_national_id || null;

export const idleWindowMs = (ctx: TrackerContext | null): number =>
  ctx && DOCUMENT_KINDS.has(ctx.surface ?? "") ? IDLE_DOCUMENT_MS : IDLE_MS;

type Open = {
  context: TrackerContext;
  startedAt: number;
  lastActiveAt: number;
};

export type ClockSignals = {
  visible: boolean;
  focused: boolean;
  /** The user's own agent turn is streaming in this tab. */
  agentWorking?: boolean;
};

export class ActivityClock {
  private context: TrackerContext | null = null;

  private open: Open | null = null;

  private pending: BeatSegment[] = [];

  private lastInputAt = 0;

  private lastTickAt = 0;

  private paused = false;

  /** Seconds counted per individual (or client) id, for the chip. */
  private totals = new Map<string, number>();

  constructor(private readonly now: () => number = Date.now) {}

  get current(): TrackerContext | null {
    return this.context;
  }

  get isPaused(): boolean {
    return this.paused;
  }

  get isCounting(): boolean {
    return this.open !== null;
  }

  secondsFor(ctx: TrackerContext | null): number {
    const key = caseKey(ctx);
    if (!key) return 0;
    const live =
      this.open && caseKey(this.open.context) === key
        ? (this.open.lastActiveAt - this.open.startedAt) / 1000
        : 0;
    return Math.round((this.totals.get(key) ?? 0) + live);
  }

  input(): void {
    this.lastInputAt = this.now();
  }

  setPaused(paused: boolean): void {
    if (paused === this.paused) return;
    if (paused) this.close();
    this.paused = paused;
  }

  /** A route change. Closes the open segment when the context differs. */
  setContext(next: TrackerContext | null): void {
    if (sameContext(this.context, next)) return;
    this.close();
    this.context = next;
  }

  /**
   * Advance the clock. Counting continues only while the tab is visible,
   * focused and recently used; otherwise the open segment ends at the last
   * moment it was active.
   */
  tick({ visible, focused, agentWorking = false }: ClockSignals): void {
    const now = this.now();
    const gap = this.lastTickAt ? now - this.lastTickAt : 0;
    this.lastTickAt = now;
    const ctx = this.context;
    const idleWindow = agentWorking
      ? Math.max(IDLE_AGENT_MS, idleWindowMs(ctx))
      : idleWindowMs(ctx);
    const active =
      !this.paused &&
      ctx !== null &&
      visible &&
      focused &&
      now - this.lastInputAt < idleWindow;
    if (!active || gap > MAX_TICK_MS) {
      this.close();
      if (!active) return;
    }
    if (!ctx) return;
    if (!this.open) {
      this.open = { context: ctx, startedAt: now, lastActiveAt: now };
    } else {
      this.open.lastActiveAt = now;
    }
  }

  /** Closed segments plus the open one cut at now; counting carries on. */
  drain(): BeatSegment[] {
    if (this.open) {
      const { context } = this.open;
      this.close();
      this.open = {
        context,
        startedAt: this.now(),
        lastActiveAt: this.now()
      };
    }
    const out = this.pending;
    this.pending = [];
    return out;
  }

  /** Put segments back after a failed send so the next flush retries them. */
  requeue(segments: BeatSegment[]): void {
    this.pending = [...segments, ...this.pending].slice(-200);
  }

  private close(): void {
    const { open } = this;
    this.open = null;
    if (!open) return;
    const seconds = (open.lastActiveAt - open.startedAt) / 1000;
    if (seconds < 1) return;
    const key = caseKey(open.context);
    if (key) this.totals.set(key, (this.totals.get(key) ?? 0) + seconds);
    this.pending.push({
      context: open.context,
      started_at: new Date(open.startedAt).toISOString(),
      ended_at: new Date(open.lastActiveAt).toISOString(),
      active_seconds: Math.round(seconds)
    });
  }
}
